import { useMemo } from 'react';
import styled from 'styled-components';

import { Metrics } from 'components/insights/constants';
import Card from 'components/insights/layout/Card';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { REFERRAL_STATUSES, validMetricValue } from '../constants';

import ReferralHighlightMetrics from './ReferralHighlightMetrics';
import ReferralHighlightsHeader from './ReferralHighlightsHeader';
import ReferralStatusDonutChart from './ReferralStatusDonutChart';

type Props = {
  loading: boolean;
  data: AnalyticsResponseData;
  rollups: AnalyticsResponseRollupCollection;
};

export default function ReferralHighlightsCard({ loading, data, rollups }: Props) {
  const overall = rollups.overall.metrics;
  const totalReferrals = overall.getCurrentDataPoint(Metrics.REVIEW_ID_COUNT, 0) ?? 0;
  const avgResponseTimeSeconds = validMetricValue(
    overall.getCurrentDataPoint(Metrics.RESPONSE_TIME_SECONDS_AVERAGE, 0)
  );
  const acceptanceRate = validMetricValue(overall.getCurrentDataPoint(Metrics.ACCEPTANCE_RATE, 0));
  const denialRate = validMetricValue(overall.getCurrentDataPoint(Metrics.DENIAL_RATE, 0));

  // Accumulate per-status counts from detail rows (grouping 0) for the donut chart.
  // ROLLUP(entity, status) doesn't produce a status-only grouping, so we sum across entities.
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = Object.fromEntries(REFERRAL_STATUSES.map((s) => [s, 0]));
    const statuses = data.dimensions.getCurrentDimensionValuesByIndex(2);
    const referralCounts = data.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT);

    statuses.forEach((status, i) => {
      if (status && status in counts) {
        counts[status] += referralCounts[i] ?? 0;
      }
    });

    return counts;
  }, [data]);

  const acceptedCount = statusCounts['accepted'];
  const deniedCount = statusCounts['rejected'];
  const respondedCount = acceptedCount + deniedCount;

  return (
    <HighlightsCard>
      <ReferralHighlightsHeader />
      <ContentRow>
        <ReferralStatusDonutChart statusCounts={statusCounts} totalReferrals={totalReferrals} loading={loading} />
        <ReferralHighlightMetrics
          acceptanceRate={acceptanceRate}
          denialRate={denialRate}
          avgResponseTimeSeconds={avgResponseTimeSeconds}
          acceptedCount={acceptedCount}
          deniedCount={deniedCount}
          totalReferrals={totalReferrals}
          respondedCount={respondedCount}
          loading={loading}
        />
      </ContentRow>
    </HighlightsCard>
  );
}

const HighlightsCard = styled(Card)`
  border-radius: 4px;
  padding: 0;
  height: 350px;
  display: flex;
  flex-direction: column;
`;

const ContentRow = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
